import React, { useState } from 'react';
import { Grid } from '@mui/material';
import { AddCircle } from '@mui/icons-material';
import {
  TextField,
  type Tag,
  TagsList,
  Dialog,
  IconButton,
  SwitchButton,
} from '../../../../../../components';
import { CreateTagDialog } from '../../GSManagement/CreateTagDialog';
import {
  PANEL_LIST_SETTING_DIALOG_TEXT,
  PANEL_TAG_TEXT,
} from '../../../../lang';

export type AddFilterListDialogProps = {
  openDialog: boolean;
  dialogEditMode: boolean;
  panelFilterName: string;
  filterTags: string[];
  conditionList: string[];
  handleCloseDialog: () => void;
  handleProcessFilter: (
    panelFilterName: string,
    tags: string[],
    remove?: boolean,
  ) => void;
  handleCreateTag: (tagName: string) => void;
};

export const AddFilterListDialog: React.FunctionComponent<
  AddFilterListDialogProps
> = (props: AddFilterListDialogProps) => {
  const {
    openDialog,
    conditionList,
    panelFilterName,
    filterTags,
    dialogEditMode,
    handleCloseDialog,
    handleProcessFilter,
    handleCreateTag,
  } = props;

  const [listName, setListName] = useState(panelFilterName);
  const [openCreateTagDialog, setOpenCreateTagDialog] = useState(false);
  const [conditions, setConditions] = useState<string[]>(filterTags);
  const [remove, setRemove] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'tags-popover' : undefined;

  const tags: Tag[] = conditionList.map(condition => {
    return {
      name: condition,
      selected: conditions.includes(condition),
    };
  });

  const handleOpenTagList = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleChangeListName = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    setListName(event.target.value);
  };

  const handleCloseTagList = (): void => {
    setAnchorEl(null);
  };

  const handleSelectTag = (tag: Tag): void => {
    if (conditions.includes(tag.name)) {
      setConditions(conditions.filter(condition => condition !== tag.name));
    } else {
      setConditions([...conditions, tag.name]);
    }
  };

  const handleDialogAction = (): void => {
    handleProcessFilter(listName, conditions, remove);
    handleCloseDialog();
  };

  const handleOpenCreateTagDialog = (): void => {
    setOpenCreateTagDialog(true);
  };

  const handleCloseTagCreateDialog = (): void => {
    setOpenCreateTagDialog(false);
  };

  const conditionText = conditions.join(', ');
  return (
    <Dialog
      openDialog={openDialog}
      dialogTitle={PANEL_LIST_SETTING_DIALOG_TEXT.LIST_SETTING}
      dialogAction={
        dialogEditMode
          ? PANEL_LIST_SETTING_DIALOG_TEXT.UPDATE
          : PANEL_LIST_SETTING_DIALOG_TEXT.CREATE
      }
      cancelAction={PANEL_LIST_SETTING_DIALOG_TEXT.CANCEL}
      onDialogClose={handleCloseDialog}
      onDialogAction={handleDialogAction}
    >
      <Grid
        container
        spacing={2}
        sx={{
          padding: '2rem',
        }}
      >
        <Grid size={3}>{PANEL_LIST_SETTING_DIALOG_TEXT.LIST_NAME}</Grid>
        <Grid size={9}>
          <TextField
            value={listName}
            onChange={handleChangeListName}
            fullWidth
          />
        </Grid>
        <Grid size={3}>{PANEL_LIST_SETTING_DIALOG_TEXT.CONDITIONS}</Grid>

        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'row',
          }}
          size={9}
        >
          <TextField value={conditionText} disabled fullWidth />
          <IconButton
            onClick={handleOpenTagList}
            sx={{
              color: '#951d1d',
            }}
            icon={<AddCircle />}
          />
        </Grid>

        {dialogEditMode && (
          <>
            <Grid size={3}>{PANEL_LIST_SETTING_DIALOG_TEXT.DELETE_LIST}</Grid>
            <Grid size={9}>
              <SwitchButton
                checked={remove}
                onChange={() => {
                  setRemove(!remove);
                }}
              />
            </Grid>
          </>
        )}
      </Grid>
      <TagsList
        id={id}
        listTitle={PANEL_LIST_SETTING_DIALOG_TEXT.TAGS_LIST}
        open={open}
        anchorEl={anchorEl}
        tags={tags}
        onClose={handleCloseTagList}
        onTagSelect={handleSelectTag}
        buttonText={PANEL_TAG_TEXT.CREATE_NEW_PANEL_TAG}
        onCreateTag={handleOpenCreateTagDialog}
      />
      <CreateTagDialog
        open={openCreateTagDialog}
        title={PANEL_TAG_TEXT.CREATE_NEW_PANEL_TAG}
        helpText={PANEL_TAG_TEXT.SET_TAGS}
        onClose={handleCloseTagCreateDialog}
        onCreate={handleCreateTag}
      />
    </Dialog>
  );
};
